import { useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { usePixiApp } from '../../services/pixiApp/pixiAppProvider';
import { useSocket } from '../../services/socket/SocketContext';
import { useAuth } from '../../services/auth/authProvider';
import { useApi } from '../../services/api/ApiProvider';
import { deleteDeck as apiDeleteDeck, claimShopItem } from '../../services/api/apiService';

import LobbyManager from '../../features/lobby/managers/lobbyManager';
import DecksManager from '../../features/decks/managers/decksManager';
import LoadingSpinnerManager from '../../features/common/managers/LoadingSpinnerManager';
import DeckEditorManager from '../../features/decks/managers/DeckEditorManager';
import CollectionManager from '../../features/collection/managers/collectionManager';
import ShopManager from '../../features/shop/managers/shopManager';
import VsScreenManager from '../../features/gameplay/managers/vsScreenManager';

const LobbyPage = () => {
    const { app, setAutoResize } = usePixiApp();
    const socket = useSocket();
    const { userName } = useAuth();
    const { decks, fetchDecks, fetchCollection, selectedDeck, setSelectedDeck, fetchShopItems, hasFreeShopItem } = useApi();
    const [isLoading, setIsLoading] = useState(true);
    const decksRef = useRef(decks);
    decksRef.current = decks;
    const hasFreeShopItemRef = useRef(hasFreeShopItem);
    hasFreeShopItemRef.current = hasFreeShopItem;

    const navigate = useNavigate();
    const location = useLocation();

    const pixiContainerRef = useRef(null);
    const lobbyManagerRef = useRef(null);
    const decksManagerRef = useRef(null);
    const loadingSpinnerManagerRef = useRef(null);

    const findMatch = () => {
        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        socket.emit('findMatch', { username: userName });
        lobbyManagerRef.current.setFindingMatch(true);
    };

    const handleMatchFound = async (data) => {
        console.log('matchFound', data);
        await VsScreenManager.loadAssets();
        navigate(`/game/${data.roomName}?findMatchMode=1`);
    };

    const openDeckSelection = () => {
        navigate('/lobby#decks');
    };

    const closeDeckSelection = () => {
        navigate(-1);
    };

    const createNewDeck = async () => {
        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        await DeckEditorManager.loadAssets();
        navigate('/decks/create');
    };

    const editDeck = async (deckId) => {
        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        await DeckEditorManager.loadAssets();
        navigate(`/decks/edit/${deckId}`);
    };

    const selectDeck = (deckId) => {
        console.log('select deck', deckId);
        setSelectedDeck(decksRef.current.find(deck => deck.id === deckId));
        navigate(-1);
    };

    const confirmDeleteDeck = async (deckId) => {
        await apiDeleteDeck(deckId);
        await fetchDecks();
    };

    const createCustomRoom = () => {
        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        socket.emit('createCustomRoom', { username: userName, deckId: localStorage.getItem('selectedDeck') });
    };

    const joinCustomRoom = () => {
        const roomName = prompt('Enter room name');
        if (!roomName) return;

        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        socket.emit('joinCustomRoom', { username: userName, roomName, deckId: localStorage.getItem('selectedDeck') });
    };

    const handleCustomRoomJoined = async (data) => {
        console.log('customRoomJoined', data);
        await VsScreenManager.loadAssets();
        navigate(`/game/${data.roomName}?findMatchMode=0`);
    };

    const joinDiscord = () => {
        window.open('https://discord.gg/TRkBnW73Bu', '_blank');
    };

    const goToShop = async () => {
        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        await ShopManager.loadStaticAssets();
        navigate('/shop');
    };

    const goToCollection = async () => {
        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        await CollectionManager.loadAssets();
        navigate('/collection');
    };

    const handleClaimStarterDeck = async (itemId) => {
        const lobbyManager = lobbyManagerRef.current;
        if (!lobbyManager) return;

        lobbyManager.setClaimingStarterDeck(itemId);
        loadingSpinnerManagerRef.current?.attachToPixiApp(app);

        try {
            const claimed = await claimShopItem(itemId);
            const updatedDecks = await fetchDecks();
            await Promise.all([fetchCollection(), fetchShopItems()]);

            const claimedDeckId = claimed?.prebuiltDeckId;
            const claimedDeck = (updatedDecks || []).find((deck) => deck.id === claimedDeckId);
            if (claimedDeck) {
                setSelectedDeck(claimedDeck);
            }

            lobbyManager.setStarterDeckSelectVisible(false);
        } catch (error) {
            console.error('Failed to claim starter deck:', error);
            alert(error.response?.data?.error || error.message || 'Failed to claim starter deck');
            lobbyManager.setClaimingStarterDeck(null);
        } finally {
            loadingSpinnerManagerRef.current?.detachFromPixiApp(app);
        }
    };

    useEffect(() => {
        const onAppResize = () => {
            if (lobbyManagerRef.current) {
                lobbyManagerRef.current.buildMainLayout(app.screen.width, app.screen.height);
            }

            if (decksManagerRef.current) {
                decksManagerRef.current.buildMainLayout(app.screen.width, app.screen.height);
                decksManagerRef.current.buildDeckList();
            }
        };

        const initLobbyPage = async () => {
            if (app && socket) {
                pixiContainerRef.current.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });

                const starterDeckItems = location.state?.starterDeckItems || [];
                const showStarterDeckSelect = starterDeckItems.length > 0 && (decks || []).length === 0;
                const lobbyManager = new LobbyManager(selectedDeck, showStarterDeckSelect, starterDeckItems);
                lobbyManager.attachToPixiApp(app);
                pixiContainerRef.current.appendChild(app.canvas);

                const decksManager = new DecksManager(decks, selectedDeck?.id);
                const loadingSpinnerManager = new LoadingSpinnerManager(app.screen.width, app.screen.height);

                const pendingLoads = [];
                if (!LobbyManager.hasLoadedAssets()) {
                    pendingLoads.push(LobbyManager.loadAssets());
                }
                if (showStarterDeckSelect) {
                    pendingLoads.push(lobbyManager.loadStarterDeckAssets());
                }
                if (!DecksManager.hasLoadedStaticAssets()) {
                    pendingLoads.push(decksManager.loadAssets());
                } else if ((decks || []).some((deck) => deck.leader && !DecksManager.hasLoadedLeader(deck.leader))) {
                    pendingLoads.push(DecksManager.loadDecksAssets(decks));
                }
                if (pendingLoads.length > 0) {
                    await Promise.all(pendingLoads);
                }

                lobbyManager.setShopBadgeVisible(hasFreeShopItemRef.current);
                lobbyManager.buildMainLayout(app.screen.width, app.screen.height);

                decksManager.buildMainLayout(app.screen.width, app.screen.height);
                decksManager.buildDeckList();

                window.addEventListener('appResize', onAppResize);

                lobbyManagerRef.current = lobbyManager;
                decksManagerRef.current = decksManager;
                loadingSpinnerManagerRef.current = loadingSpinnerManager;

                lobbyManager.onFindMatchButtonClick = findMatch;
                lobbyManager.onDeckSelectionButtonClick = openDeckSelection;
                lobbyManager.onCreateCustomRoomButtonClick = createCustomRoom;
                lobbyManager.onJoinCustomRoomButtonClick = joinCustomRoom;
                lobbyManager.onJoinDiscordButtonClick = joinDiscord;
                lobbyManager.onGoToShopButtonClick = goToShop;
                lobbyManager.onGoToCollectionButtonClick = goToCollection;
                lobbyManager.onClaimStarterDeck = handleClaimStarterDeck;

                decksManager.onBackButtonClick = closeDeckSelection;
                decksManager.onNewDeckButtonClick = createNewDeck;
                decksManager.onEditDeckButtonClick = editDeck;
                decksManager.onSelectDeckButtonClick = selectDeck;
                decksManager.onConfirmDeleteDeck = confirmDeleteDeck;

                if (location.hash === '#decks') {
                    decksManager.attachToPixiApp(app);
                }

                socket.on('matchFound', handleMatchFound);
                socket.on('customRoomJoined', handleCustomRoomJoined);

                setIsLoading(false);
            }
        }

        setAutoResize(true);
        initLobbyPage();

        return () => {
            if (lobbyManagerRef.current && app) {
                lobbyManagerRef.current.detachFromPixiApp(app);
            }

            if (decksManagerRef.current && app) {
                decksManagerRef.current.detachFromPixiApp(app);
            }

            if (loadingSpinnerManagerRef.current && app) {
                loadingSpinnerManagerRef.current.detachFromPixiApp(app);
            }

            if (socket) {
                socket.off('matchFound', handleMatchFound);
                socket.off('customRoomJoined', handleCustomRoomJoined);
            }

            window.removeEventListener('appResize', onAppResize);

            setAutoResize(false);
        };
    }, [app, socket]);

    useEffect(() => {
        if (decksManagerRef.current && decks) {
            decksManagerRef.current.setDecks(decks);
        }
    }, [decks]);

    useEffect(() => {
        lobbyManagerRef.current?.setShopBadgeVisible(hasFreeShopItem);
    }, [hasFreeShopItem]);

    useEffect(() => {
        const lobbyManager = lobbyManagerRef.current;
        if (!lobbyManager || !selectedDeck || isLoading) return;

        let cancelled = false;
        (async () => {
            if (!DecksManager.hasLoadedLeader(selectedDeck.leader)) {
                await DecksManager.loadDecksAssets([selectedDeck]);
            }
            if (!cancelled) {
                lobbyManager.setSelectedDeck(selectedDeck);
                decksManagerRef.current?.setSelectedDeckId(selectedDeck.id);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isLoading, selectedDeck]);

    useEffect(() => {
        if (!decksManagerRef.current) return;
        
        if (location.hash === '#decks') {
            decksManagerRef.current.attachToPixiApp(app);
        } else if (location.hash === '') {
            decksManagerRef.current.detachFromPixiApp(app);
        }
    }, [location]);

    return (
        <div style={{ width: '100dvw', height: '100dvh', overflow: 'hidden' }} ref={pixiContainerRef} />
    );
};

export default LobbyPage;